import { useEmbedContext } from '@thoughtspot/blink-context';
import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import { useTranslation } from '@thoughtspot/i18n';
import { isUnsavedChangesModalEnabledForAdhocAnswer } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useCreateAnswerSessionMutation } from '/@services/answer/answer-service';
import { useLoadExistingAnswerSession } from '/@services/bach-ac-history/bach-ac-history-service';
import {
    BachSessionId,
    SageSearchResponse,
} from '/@services/generated/graphql-types';
import { IncomingEventType, SearchParams } from '/@utils/embed.util';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { useRuntimeParams } from '/@utils/runtime-filters.util';
import {
    clearUnsavedAnswerOauthContext,
    readUnsavedAnswerOauthContext,
    UnsavedAnswerOauthContext,
} from '../components/embrace/embrace-oauth/unsaved-answer-oauth-context.util';
import { NavigationModalContainerProps } from '../components/navigation-blocker/navigation-blocker-modal.container';
import { isQueryEmptyWithoutSpaces } from '../components/sage-search-bar/sage-search-box/sage-search-box.util';
import { BaseAnswerLayoutContextValue } from './base-answer-layout-context';

export type AdhocAnswerLayoutContextValue = BaseAnswerLayoutContextValue;

const AdhocAnswerLayoutContext = createContext<AdhocAnswerLayoutContextValue | null>(
    null,
);
AdhocAnswerLayoutContext.displayName = 'AdhocAnswerLayoutContext';

const AdhocAnswerLayoutProviderInner: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { t } = useTranslation();
    const runtimeParams = useRuntimeParams();
    const {
        useEmbedEventSubscription,
        embedParams: { dataSources: dataSourcesFromEmbedParams },
    } = useEmbedContext();

    const [answerSessionId, setAnswerSessionId] = useState('');
    const [answerId, setAnswerId] = useState('');
    const [dataSourceIds, setDataSourceIds] = useState(
        dataSourcesFromEmbedParams || [],
    );
    const [sourceUpdatedByHostEvent, setSourceUpdatedByHostEvent] = useState(
        false,
    );

    // Persisted across the Answer<->Spreadsheet child-route remount (this
    // provider stays mounted). Lets the Answer view restore sage tokens and
    // skip auto-focus when returning from the Spreadsheet.
    const [
        cachedSageQueryState,
        setCachedSageQueryState,
    ] = useState<SageSearchResponse | null>(null);
    const [hasVisitedSpreadsheet, setHasVisitedSpreadsheet] = useState(false);
    const markSpreadsheetVisited = useCallback(
        () => setHasVisitedSpreadsheet(true),
        [],
    );

    const dataPaginationParams = {
        isClientPaginated: true,
        offset: 0,
        size: envFlags.getValue(FLAGS.dataBatchSize?.name) || 1000,
    };

    const [
        createSession,
        { data, loading, error },
    ] = useCreateAnswerSessionMutation(
        (session: BachSessionId, newAnswerId: string) => {
            setAnswerSessionId(session.sessionId);
            setAnswerId(newAnswerId);
        },
        dataSourceIds,
        !(dataSourcesFromEmbedParams || sourceUpdatedByHostEvent),
        dataPaginationParams,
    );

    // One-shot pointer to the answer session saved before an in-page OAuth
    // redirect; present only when landing back from the IDP on the same URL
    // it was saved from. Re-attaching to it preserves the unsaved search
    // context that a fresh Answer__create session would lose.
    const [
        pendingOauthRestore,
        setPendingOauthRestore,
    ] = useState<UnsavedAnswerOauthContext | null>(
        readUnsavedAnswerOauthContext,
    );
    const [restoredSessionId, setRestoredSessionId] = useState('');
    const oauthRestoreInFlightRef = useRef(false);

    const {
        loadAnswerSession: restoreAnswerSession,
        data: restoredAnswer,
        loading: restoreLoading,
        error: restoreError,
    } = useLoadExistingAnswerSession(
        pendingOauthRestore?.sessionId ?? '',
        (sessionId: string, restoredAnswerId: string) => {
            oauthRestoreInFlightRef.current = false;
            setRestoredSessionId(sessionId);
            setAnswerSessionId(sessionId);
            setAnswerId(restoredAnswerId);
        },
        false,
        false,
    );

    const navModalProps:
        | NavigationModalContainerProps
        | false = isUnsavedChangesModalEnabledForAdhocAnswer() && {
        title: t('documentUnsavedChangesAlert.UNSAVED_CHANGES'),
        navServicePromptMessage: t('answer.unsavedChangesAlert.message'),
        confirmButton: {},
        denyButton: {},
        dismissButton: {},
    };

    // Handles the incoming search event to refresh the data sources.
    // If the payload includes new data sources but no search query,
    // it updates dataSourceIds and creates a new session.
    // If the search query is provided, it will be managed by
    // use-search-embed-events.ts.
    useEmbedEventSubscription(
        IncomingEventType.Search,
        (payload: SearchParams) => {
            const { searchQuery, dataSources } = payload || {};
            const isValidQuery =
                !searchQuery || isQueryEmptyWithoutSpaces(searchQuery);
            if (!_.isEmpty(dataSources) && isValidQuery) {
                setSourceUpdatedByHostEvent(true);
                // Clear session immediately so the loading gate
                // (!answerSessionId) activates before createSession() fires.
                setAnswerSessionId('');
                setDataSourceIds(dataSources);
            }
        },
    );

    useEffect(() => {
        // Consume the saved OAuth-redirect pointer exactly once; later
        // dependency changes (e.g. a host event swapping data sources)
        // create fresh sessions as before.
        if (pendingOauthRestore) {
            setPendingOauthRestore(null);
            clearUnsavedAnswerOauthContext();
            oauthRestoreInFlightRef.current = true;
            restoreAnswerSession(
                pendingOauthRestore.sessionId,
                pendingOauthRestore.genNo,
                pendingOauthRestore.acSessionId,
                pendingOauthRestore.acGenNo,
            );
            return;
        }
        // Don't clobber an in-flight restore with a fresh empty session.
        if (oauthRestoreInFlightRef.current) {
            return;
        }
        createSession();
    }, [
        createSession,
        dataSourceIds,
        runtimeParams,
        pendingOauthRestore,
        restoreAnswerSession,
    ]);

    // Fall back to a fresh session when the saved session expired server-side.
    useEffect(() => {
        if (restoreError) {
            oauthRestoreInFlightRef.current = false;
            createSession();
        }
    }, [restoreError, createSession]);

    return (
        <AdhocAnswerLayoutContext.Provider
            value={{
                answerSessionId,
                answerId,
                data,
                // Restore loading is included so consumers gate on "a session
                // is being established" regardless of which mutation runs it.
                loading: loading || restoreLoading,
                error,
                navModalProps,
                cachedSageQueryState,
                setCachedSageQueryState,
                hasVisitedSpreadsheet,
                markSpreadsheetVisited,
                restoredAnswer,
                restoredSessionId,
            }}
        >
            {children}
        </AdhocAnswerLayoutContext.Provider>
    );
};

export const AdhocAnswerLayoutProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    // In embedded app contexts (full-app embed), skip the layout provider so
    // AdhocAnswer falls back to standalone mode. Spreadsheet and the shared
    // session-creation logic are not supported in embedded contexts.
    if (isEmbeddedApp()) {
        return <>{children}</>;
    }
    return (
        <AdhocAnswerLayoutProviderInner>
            {children}
        </AdhocAnswerLayoutProviderInner>
    );
};

export const useAdhocAnswerLayoutContext = (): AdhocAnswerLayoutContextValue => {
    const ctx = useContext(AdhocAnswerLayoutContext);
    if (ctx === null) {
        throw new Error(
            'useAdhocAnswerLayoutContext must be used within AdhocAnswerLayoutProvider',
        );
    }
    return ctx;
};

/**
 * Returns the layout context when available, or null when AdhocAnswer is
 * rendered standalone (without AdhocAnswerLayoutProvider as an ancestor).
 *
 * Backward-compatibility shim — callers that do not yet wrap AdhocAnswer
 * in AdhocAnswerLayoutProvider will get null here and fall back to their
 * own loading logic.
 *
 * TODO: once every callsite is migrated to use AdhocAnswerLayoutProvider,
 * remove this hook and replace usages with useAdhocAnswerLayoutContext.
 */
export const useOptionalAdhocAnswerLayoutContext = (): AdhocAnswerLayoutContextValue | null =>
    useContext(AdhocAnswerLayoutContext);
